<script setup>
import { iniciaisDoNome, corDoNome } from 'dashboard/helper/avatar';
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { legalActivitiesAPI } from 'dashboard/api/legalActivities';
import { LegalProcessesApi } from 'dashboard/api/legalProcesses';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import AdvupSelect from 'dashboard/components-next/AdvupSelect.vue';
import AdvupDateTimeInput from 'dashboard/components-next/AdvupDateTimeInput.vue';

// Lançar o atendimento na agenda SEM sair da conversa (pedido do Dr. Henrique,
// 02/09/2026: "seria possível criar uma ferramenta dentro da conversa para
// lançar o atendimento direto na agenda?").
//
// POR QUE ESTE MODAL EXISTE, se já havia um botão "Agendar atendimento"
// ------------------------------------------------------------------------
// O botão existia e abria o AppointmentBookingModal, que cria um `Appointment`
// — outro módulo, que exige profissional e serviço cadastrados. Medido em
// produção em 02/09:
//
//   appointments   = 0      profissionais = 0     servicos = 0
//   activities     = 4539   <- a agenda que o escritório de fato usa
//
// Ou seja: o botão nunca teve como funcionar. Aqui ele passa a gravar onde os
// 4.539 compromissos moram, que é a agenda jurídica (Activity).
//
// O QUE VEM PRONTO E O QUE NÃO VEM (decisões do Victor, 02/09)
// ------------------------------------------------------------
// Vem: cliente (o contato da conversa) e responsável (quem está atendendo).
// NÃO vem: data e hora — quem agenda digita. Ler a data do texto da conversa
// foi descartado de propósito: um compromisso na data errada é pior que um
// campo vazio.
// NÃO vem: processo — fica opcional, sem pré-seleção, porque o cliente pode
// ter vários e escolher por ele erraria.

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  contact: { type: Object, default: () => ({}) },
  // Quando vem preenchido, o modal edita em vez de criar. Pedido do Victor
  // (02/09): clicar no compromisso da barra lateral tem que permitir corrigir
  // uma informação sem sair da conversa — mandar para a agenda para editar
  // quebraria o propósito da ferramenta, que é não interromper o atendimento.
  activity: { type: Object, default: null },
});

const emit = defineEmits(['close', 'created']);

const { t } = useI18n();
const store = useStore();

const salvando = ref(false);
const processos = ref([]);

const form = ref({
  description: '',
  start_at: '',
  duracaoMinutos: 60,
  // `location` sai da TELA (virou "Executante", pedido da Dra. Jullya
  // 07/09/2026) mas continua no estado: ao editar um compromisso que já tinha
  // local, o valor é lido e devolvido intacto. Some do formulário, não do
  // banco — e o agendamento completo da Agenda continua oferecendo o campo.
  location: '',
  executanteId: '',
  legal_process_id: '',
  observations: '',
});

// EXECUTANTE — quem vai atender, que é diferente de quem marcou.
//
// Pedido da Dra. Jullya (07/09/2026): "Ao invés de local colocar executante
// para aparecer nas agendas certo para quem vai atender e nao quem marca".
//
// O comentário no topo deste arquivo dizia, desde 02/09, que o modal já vinha
// com "responsável (quem está atendendo)". NÃO vinha: o payload nunca mandou
// `involveds`, então o compromisso nascia sem envolvido nenhum e não aparecia
// na agenda de ninguém (armadilha 1.18 — comentário que promete e envelhece).
//
// A agenda do escritório agrupa por `responsible` (ver AgendaEscritorio.vue),
// que sai de um `involved` com `role_responsible`. É o mesmo par de papéis
// que o agendamento completo da Agenda grava.
const agentes = computed(() => store.getters['agents/getVerifiedAgents'] || []);

const executanteOptions = computed(() =>
  agentes.value.map(a => ({ value: String(a.id), label: a.name }))
);

const duracaoOptions = computed(() => [
  { value: 30, label: t('CONVERSATION.SCHEDULE_ACTIVITY.DURATION_30') },
  { value: 60, label: t('CONVERSATION.SCHEDULE_ACTIVITY.DURATION_60') },
  { value: 90, label: t('CONVERSATION.SCHEDULE_ACTIVITY.DURATION_90') },
  { value: 120, label: t('CONVERSATION.SCHEDULE_ACTIVITY.DURATION_120') },
]);

// A opção vazia é o padrão: o Victor pediu explicitamente que o processo NÃO
// venha pré-selecionado.
const processoOptions = computed(() => [
  { value: '', label: t('CONVERSATION.SCHEDULE_ACTIVITY.PROCESS_NONE') },
  ...processos.value.map(p => ({
    value: String(p.id),
    label: p.number_cnj ? `${p.title} — ${p.number_cnj}` : p.title,
  })),
]);

const carregarProcessos = async id => {
  if (!id) return;
  try {
    // Mesma chamada que a lista "Processos vinculados" da barra lateral já usa
    // (ContactLegalProcesses.vue) — não inventa endpoint novo.
    const { data } = await LegalProcessesApi.getAll({ contactId: id });
    processos.value = data?.payload || [];
  } catch {
    // Silencioso: sem a lista o campo fica só com "nenhum". Processo é
    // opcional, e o compromisso não pode deixar de ser criado por causa disso.
    processos.value = [];
  }
};

// `datetime-local` não aceita ISO com fuso: precisa de 'YYYY-MM-DDTHH:mm' na
// hora LOCAL. Cortar a string do ISO mostraria a hora em UTC — 3 horas erradas.
const paraCampoLocal = iso => {
  if (!iso) return '';
  const d = new Date(iso);
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const minutosEntre = (inicio, fim) => {
  if (!inicio || !fim) return 60;
  return Math.round((new Date(fim) - new Date(inicio)) / 60000) || 60;
};

watch(
  () => [props.contact?.id, props.activity?.id],
  () => {
    const a = props.activity;
    if (a) {
      const executor = (a.involveds || []).find(
        iv =>
          iv.involvable_type === 'User' &&
          (iv.role_executor || iv.role_responsible)
      );
      form.value = {
        description: a.description || '',
        start_at: paraCampoLocal(a.start_at),
        duracaoMinutos: minutosEntre(a.start_at, a.end_at),
        location: a.location || '',
        executanteId: executor ? String(executor.involvable_id) : '',
        legal_process_id: a.legal_process_id ? String(a.legal_process_id) : '',
        observations: a.observations || '',
      };
    } else {
      form.value.description = t(
        'CONVERSATION.SCHEDULE_ACTIVITY.DEFAULT_TITLE',
        { nome: props.contact?.name || '' }
      );
      // Quem marca costuma ser quem atende — vem preenchido, mas trocável.
      const eu = store.getters.getCurrentUser;
      if (eu?.id) form.value.executanteId = String(eu.id);
    }
    carregarProcessos(props.contact?.id);
  },
  { immediate: true }
);

const editando = computed(() => !!props.activity?.id);

const podeSalvar = computed(
  () => !!form.value.description.trim() && !!form.value.start_at
);

const somarMinutos = (isoLocal, minutos) => {
  const d = new Date(isoLocal);
  d.setMinutes(d.getMinutes() + Number(minutos || 60));
  return d.toISOString();
};

// A nota fica na conversa para quem atender depois saber que já existe
// compromisso marcado. `private: true` é o ponto: o Victor pediu que SÓ a
// equipe veja — nada disto chega ao cliente pelo WhatsApp.
const registrarNotaInterna = dataFormatada => {
  return store.dispatch('createPendingMessageAndSend', {
    conversationId: props.conversationId,
    message: t('CONVERSATION.SCHEDULE_ACTIVITY.INTERNAL_NOTE', {
      titulo: form.value.description,
      quando: dataFormatada,
    }),
    private: true,
  });
};

const salvar = async () => {
  if (!podeSalvar.value || salvando.value) return;
  salvando.value = true;

  try {
    const inicio = new Date(form.value.start_at);
    const payload = {
      legal_activity: {
        activity_type: 'commitment',
        description: form.value.description.trim(),
        start_at: inicio.toISOString(),
        end_at: somarMinutos(form.value.start_at, form.value.duracaoMinutos),
        contact_id: props.contact?.id || null,
        location: form.value.location || null,
        observations: form.value.observations || null,
        legal_process_id: form.value.legal_process_id || null,
      },
    };

    // `responsible` E `executor` no mesmo envolvido, igual ao que o
    // agendamento completo da Agenda grava: a agenda do escritório agrupa
    // pelo primeiro, e "executante" é o nome que o escritório usa para o
    // segundo. Sem isto o compromisso nasce órfão e não aparece em agenda
    // nenhuma — era o defeito que a Dra. Jullya viu.
    if (form.value.executanteId) {
      payload.involveds = [
        {
          involvable_type: 'User',
          involvable_id: Number(form.value.executanteId),
          role_responsible: true,
          role_executor: true,
        },
      ];
    }

    if (editando.value) {
      await legalActivitiesAPI.update(props.activity.id, payload);
    } else {
      await legalActivitiesAPI.create(payload);
    }

    const quando = inicio.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

    // Nota SÓ na criação. Numa edição ela viraria ruído: corrigir o horário
    // três vezes encheria a conversa de avisos repetidos.
    //
    // A nota é um extra: se falhar, o compromisso JÁ existe e não pode ser
    // desfeito por causa dela. Por isso num try próprio.
    if (!editando.value) {
      try {
        await registrarNotaInterna(quando);
      } catch {
        // segue em frente — o compromisso está salvo
      }
    }

    useAlert(
      t(
        editando.value
          ? 'CONVERSATION.SCHEDULE_ACTIVITY.UPDATED'
          : 'CONVERSATION.SCHEDULE_ACTIVITY.CREATED'
      )
    );
    // A barra lateral escuta este evento para recarregar sozinha.
    emitter.emit(BUS_EVENTS.SCHEDULED_ACTIVITY_SAVED);
    emit('created');
    emit('close');
  } catch (erro) {
    const msg =
      erro?.response?.data?.errors?.join(', ') ||
      t('CONVERSATION.SCHEDULE_ACTIVITY.ERROR');
    useAlert(msg);
  } finally {
    salvando.value = false;
  }
};
</script>

<template>
  <div class="advup-modal-fundo" @click.self="emit('close')">
    <div
      v-modal-acessivel="
        () => {
          emit('close');
        }
      "
      :data-rascunho="
        activity?.id ? undefined : `conversa:${conversationId}:agendar`
      "
      class="advup-modal advup-modal--claro advup-modal--estreito advup-modal--teto"
    >
      <!-- Topo em ambar: agendar e sobre PRAZO, e ambar e o tom de prazo no
           produto inteiro — o mesmo do prazo fatal e do "Vence hoje". -->
      <div class="advup-modal__topo advup-modal__topo--ambar">
        <!-- Topo claro da Agenda (§5.10), como nos outros módulos — era o
             degradê escuro (05/10/2026). -->
        <i
          class="advup-modal__marca i-lucide-calendar-clock"
          aria-hidden="true"
        />
        <span class="advup-modal__selo" aria-hidden="true">
          <i class="i-lucide-calendar-clock" />
        </span>
        <div class="min-w-0 flex-1">
          <h2 class="advup-modal__titulo">
            {{
              t(
                editando
                  ? 'CONVERSATION.SCHEDULE_ACTIVITY.TITLE_EDIT'
                  : 'CONVERSATION.SCHEDULE_ACTIVITY.TITLE'
              )
            }}
          </h2>
        </div>
        <button
          type="button"
          class="advup-modal__fechar"
          :aria-label="t('CONVERSATION.SCHEDULE_ACTIVITY.CANCEL')"
          @click="emit('close')"
        >
          <i class="i-lucide-x" />
        </button>
      </div>

      <div class="advup-modal__corpo sam-corpo">
        <!-- Cliente: só leitura. Vem da conversa, não se escolhe aqui — e por
             isso não tem cara de campo: é o DADO, com o rosto da pessoa. -->
        <div class="sam-cliente">
          <span
            class="advup-avatar advup-avatar--m"
            :class="`advup-avatar--${corDoNome(contact?.name)}`"
            aria-hidden="true"
          >
            {{ iniciaisDoNome(contact?.name) }}
          </span>
          <span class="sam-cliente__txt">
            <span class="advup-rotulo">
              {{ t('CONVERSATION.SCHEDULE_ACTIVITY.CLIENT') }}
            </span>
            <b v-if="contact?.name" class="sam-cliente__nome">
              {{ contact.name }}
            </b>
            <span v-else class="advup-nulo" aria-hidden="true">—</span>
          </span>
        </div>

        <div class="sam-campo">
          <label class="advup-rotulo" for="sam-assunto">
            {{ t('CONVERSATION.SCHEDULE_ACTIVITY.DESCRIPTION') }}
          </label>
          <input
            id="sam-assunto"
            v-model="form.description"
            type="text"
            class="advup-campo reset-base"
          />
        </div>

        <div class="sam-grade">
          <div class="sam-campo">
            <span class="advup-rotulo" aria-hidden="true">
              {{ t('CONVERSATION.SCHEDULE_ACTIVITY.WHEN') }}
            </span>
            <!-- Era `type="datetime-local"`, o seletor nativo do Chrome: cinza,
                 fora do idioma da máquina e listando os 60 minutos. O advup
                 tem o seu desde 08/2026; este modal tinha ficado para trás.
                 `minute-step="30"` atende o pedido da Dra. Jullya de horários
                 redondos (07/09). -->
            <AdvupDateTimeInput
              v-model="form.start_at"
              :minute-step="30"
              no-past
            />
          </div>
          <div class="sam-campo">
            <span class="advup-rotulo" aria-hidden="true">
              {{ t('CONVERSATION.SCHEDULE_ACTIVITY.DURATION') }}
            </span>
            <AdvupSelect
              v-model="form.duracaoMinutos"
              :options="duracaoOptions"
              :rotulo="t('CONVERSATION.SCHEDULE_ACTIVITY.DURATION')"
              min-width="100%"
            />
          </div>
        </div>

        <div class="sam-campo">
          <span class="advup-rotulo" aria-hidden="true">
            {{ t('CONVERSATION.SCHEDULE_ACTIVITY.EXECUTOR') }}
          </span>
          <AdvupSelect
            v-model="form.executanteId"
            :options="executanteOptions"
            :rotulo="t('CONVERSATION.SCHEDULE_ACTIVITY.EXECUTOR')"
            min-width="100%"
          />
          <p class="advup-nota sam-nota">
            {{ t('CONVERSATION.SCHEDULE_ACTIVITY.EXECUTOR_HINT') }}
          </p>
        </div>

        <div class="sam-campo">
          <span class="advup-rotulo" aria-hidden="true">
            {{ t('CONVERSATION.SCHEDULE_ACTIVITY.PROCESS') }}
          </span>
          <AdvupSelect
            v-model="form.legal_process_id"
            :options="processoOptions"
            :rotulo="t('CONVERSATION.SCHEDULE_ACTIVITY.PROCESS')"
            min-width="100%"
          />
        </div>

        <div class="sam-campo">
          <label class="advup-rotulo" for="sam-obs">
            {{ t('CONVERSATION.SCHEDULE_ACTIVITY.NOTES') }}
          </label>
          <textarea
            id="sam-obs"
            v-model="form.observations"
            rows="2"
            class="advup-campo advup-campo--area"
          />
        </div>

        <!-- Nada vai para o cliente: fica uma nota interna na conversa. Dito
             com o cadeado, que é o sinal de "só a equipe vê" no produto. -->
        <p v-if="!editando" class="sam-privado">
          <i class="i-lucide-lock sam-privado__ico" aria-hidden="true" />
          {{ t('CONVERSATION.SCHEDULE_ACTIVITY.PRIVACY_HINT') }}
        </p>
      </div>

      <div class="advup-modal__rodape advup-modal__rodape--acoes">
        <button
          type="button"
          class="advup-btn advup-btn--vazado"
          @click="emit('close')"
        >
          {{ t('CONVERSATION.SCHEDULE_ACTIVITY.CANCEL') }}
        </button>
        <button
          type="button"
          class="advup-btn-forte"
          :disabled="!podeSalvar || salvando"
          @click="salvar"
        >
          <i
            :class="
              salvando
                ? 'i-lucide-loader-2 animate-spin'
                : 'i-lucide-calendar-check'
            "
            aria-hidden="true"
          />
          {{
            t(
              editando
                ? 'CONVERSATION.SCHEDULE_ACTIVITY.SAVE_EDIT'
                : 'CONVERSATION.SCHEDULE_ACTIVITY.SAVE'
            )
          }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.sam-corpo {
  display: flex;
  flex-direction: column;
  gap: var(--esp-4);
}

.sam-campo {
  display: flex;
  flex-direction: column;
  gap: var(--esp-1);
  min-width: 0;
}

.sam-grade {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--esp-3);
}

.sam-nota {
  margin: 0;
}

/* O cliente é o dado da conversa, com o rosto dele. */
.sam-cliente {
  display: flex;
  align-items: center;
  gap: var(--esp-3);
  padding: var(--esp-3);
  border: 1px solid rgb(var(--slate-4));
  border-radius: var(--r-4);
  background: rgb(var(--slate-2));
}

.sam-cliente__txt {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.sam-cliente__nome {
  overflow: hidden;
  font-size: var(--txt-dado);
  font-weight: var(--peso-forte);
  color: rgb(var(--slate-12));
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sam-privado {
  display: flex;
  align-items: flex-start;
  gap: var(--esp-2);
  margin: 0;
  font-size: var(--txt-nota);
  line-height: var(--linha-solta);
  color: rgb(var(--slate-11));
}

.sam-privado__ico {
  width: var(--ico-1);
  height: var(--ico-1);
  flex-shrink: 0;
  margin-top: calc((1lh - var(--ico-1)) / 2);
  color: rgb(var(--amber-11));
}

/* No celular, quando e quanto tempo empilham: lado a lado, o seletor de data
   ficava com 140px e cortava o horário. */
@media (max-width: 479px) {
  .sam-grade {
    grid-template-columns: 1fr;
  }
}
</style>
